import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';

import { opsManagementApi } from '@/features/admin/api/opsManagementApi';
import type { AlertEventRow } from '@/features/admin/types/opsExtended.types';
import { store } from '@/stores';

import type { IoTDataPoint } from '../types/realtime.types';

const ACKED_STATION_IDS_KEY = 'ies-iot-threshold-acked-station-ids-v1';

export interface IotAlertAckEntry {
  stationId: string;
  eventId: string;
}

export interface AcknowledgeIotAlertsResult {
  acked: IotAlertAckEntry[];
  /** `station_id` không có bản ghi `open` trên máy chủ (hoặc lỗi API). */
  noOpen: string[];
}

export function iotStationDisplayName(stations: IoTDataPoint[], stationId: string): string {
  const hit = stations.find((s) => s.id === stationId);
  return (hit?.name ?? hit?.sensorId ?? stationId).trim() || stationId;
}

export function formatIotStationNameList(stations: IoTDataPoint[], ids: string[]): string {
  return ids.map((id) => iotStationDisplayName(stations, id)).join(' · ');
}

export function loadAckedIotStationIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(ACKED_STATION_IDS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string' && x.trim() !== ''));
  } catch {
    return new Set();
  }
}

export function saveAckedIotStationIds(ids: Set<string>): void {
  try {
    sessionStorage.setItem(ACKED_STATION_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function markIotStationsAcked(stationIds: string[]): void {
  if (stationIds.length === 0) return;
  const next = loadAckedIotStationIds();
  for (const id of stationIds) next.add(id);
  saveAckedIotStationIds(next);
}

/** Bỏ đánh dấu UI khi trạm không còn vượt ngưỡng. */
export function pruneAckedIotStationIds(activeBreachedIds: Iterable<string>): void {
  const active = new Set(activeBreachedIds);
  const prev = loadAckedIotStationIds();
  let changed = false;
  for (const id of prev) {
    if (!active.has(id)) {
      prev.delete(id);
      changed = true;
    }
  }
  if (changed) saveAckedIotStationIds(prev);
}

/** Đọc + dọn sessionStorage theo danh sách trạm đang vượt ngưỡng (dùng trong useMemo). */
export function ackedIotStationIdsForBreaches(activeBreachedIds: string[]): Set<string> {
  pruneAckedIotStationIds(activeBreachedIds);
  return loadAckedIotStationIds();
}

const OPEN_EVENTS_PAGE_SIZE = 50;
const MAX_OPEN_EVENT_PAGES = 5;
/** Một lần bấm / một trạm — tránh hàng trăm PATCH khi BE còn nhiều bản ghi `open` cũ. */
const MAX_ACK_EVENTS_PER_STATION = 1;

let bulkAckDepth = 0;
let inflightAck: Promise<AcknowledgeIotAlertsResult> | null = null;

/** WS `alert_event` bỏ qua invalidate lịch sử khi đang bulk ACK (tránh bão refetch). */
export function isBulkAlertAckInProgress(): boolean {
  return bulkAckDepth > 0;
}

function eventTimeMs(row: AlertEventRow): number {
  const raw = row.create_date;
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

/** Lấy event `open` cần ACK — dedupe, lọc `station_id`, chỉ giữ bản ghi mới nhất. */
async function fetchOpenAlertEventsForStation(stationId: string): Promise<string[]> {
  const sid = stationId.trim();
  const collected: AlertEventRow[] = [];
  const seenIds = new Set<string>();

  for (let page = 1; page <= MAX_OPEN_EVENT_PAGES; page += 1) {
    const paged = await store
      .dispatch(
        opsManagementApi.endpoints.getAlertHistory.initiate(
          {
            status: 'open',
            station_id: sid,
            page,
            pageSize: OPEN_EVENTS_PAGE_SIZE,
          },
          { subscribe: false },
        ),
      )
      .unwrap();

    let added = 0;
    for (const row of paged.items) {
      if (!row?.id || seenIds.has(row.id)) continue;
      const rowSid = (row.station_id ?? '').trim();
      if (rowSid && rowSid !== sid) continue;
      seenIds.add(row.id);
      collected.push(row);
      added += 1;
    }

    if (added === 0 || paged.items.length < OPEN_EVENTS_PAGE_SIZE) break;
    if (paged.total != null && seenIds.size >= paged.total) break;
  }

  collected.sort((a, b) => eventTimeMs(b) - eventTimeMs(a));
  return collected.slice(0, MAX_ACK_EVENTS_PER_STATION).map((r) => r.id);
}

async function runAcknowledgeOpenAlertsForIotStations(
  stations: IoTDataPoint[],
): Promise<AcknowledgeIotAlertsResult> {
  const acked: IotAlertAckEntry[] = [];
  const noOpen: string[] = [];

  for (const s of stations) {
    try {
      const eventIds = await fetchOpenAlertEventsForStation(s.id);
      if (eventIds.length === 0) {
        noOpen.push(s.id);
        continue;
      }
      for (const eventId of eventIds) {
        await store
          .dispatch(
            opsManagementApi.endpoints.patchAlertAck.initiate(
              {
                id: eventId,
                body: { status: 'acknowledged' },
                silentInvalidate: true,
              },
              { track: false },
            ),
          )
          .unwrap();
        acked.push({ stationId: s.id, eventId });
      }
    } catch {
      noOpen.push(s.id);
    }
  }

  if (acked.length > 0) {
    markIotStationsAcked([...new Set(acked.map((a) => a.stationId))]);
    store.dispatch(
      opsManagementApi.util.invalidateTags([
        { type: 'OpsAlert', id: 'HISTORY' },
        { type: 'ReportSummary', id: 'DASHBOARD' },
      ]),
    );
  }

  return { acked, noOpen };
}

/**
 * Với mỗi trạm: ACK event `open` mới nhất (`station_id`) — một nút xác nhận / trạm.
 * Gộp request đồng thời; chặn gọi chồng khi user bấm liên tục.
 */
export function acknowledgeOpenAlertsForIotStations(
  stations: IoTDataPoint[],
): Promise<AcknowledgeIotAlertsResult> {
  if (inflightAck) return inflightAck;

  bulkAckDepth += 1;
  inflightAck = runAcknowledgeOpenAlertsForIotStations(stations).finally(() => {
    bulkAckDepth = Math.max(0, bulkAckDepth - 1);
    if (bulkAckDepth === 0) inflightAck = null;
  });

  return inflightAck;
}

export function showIotAlertAckFeedback(
  messageApi: MessageInstance,
  t: TFunction,
  result: AcknowledgeIotAlertsResult,
  stations: IoTDataPoint[],
): void {
  if (result.acked.length > 0) {
    const names = formatIotStationNameList(stations, [
      ...new Set(result.acked.map((a) => a.stationId)),
    ]);
    messageApi.success(`${t('realtime.iotNetwork.headerDialogAckSuccess')} — ${names}`, 5);
  }

  if (result.acked.length === 0) {
    messageApi.warning(t('realtime.iotNetwork.headerDialogAckNoOpen'));
    return;
  }

  if (result.noOpen.length > 0) {
    messageApi.info(
      `${t('realtime.iotNetwork.headerDialogAckSkippedTitle')} — ${formatIotStationNameList(stations, result.noOpen)}`,
      6,
    );
  }
}
